import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
  Divider,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  ShoppingCart as ShoppingCartIcon,
  LocalMall as LocalMallIcon,
  Print as PrintIcon,
  Visibility as VisibilityIcon,
  LockOutlined as LockOutlinedIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import {
  tienePermisosProgramaCompras,
  puedeEditarProgramaFacturacion,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import { reglaDescuentoAplicaAProducto } from 'utils/reglasDescuentos';
import VistaCompra from './VistaCompra';
import Detallecompras from './Detallecompras';

/** Clave del programa para validar permisos sobre Registro de Compras. */
export const PROGRAMA_REGISTRO_COMPRAS = 'registrocompras';

const TIPO_FACTURA_CREDITO = 'Factura Credito';
const TIPO_FACTURA_CONTADO = 'Factura Contado';
const TIPO_COMPRA_CREDITO = 'Compra Credito';
const TIPO_COMPRA_CONTADO = 'Compra Contado';

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CONFIGURACION_PLANTILLA_DEFAULT = {
  plantillaPorDefecto: '80mm-basica',
  leyendaPiePagina: '',
  mensajeGracias: '',
  nombreEmpresa: '',
  mostrarObservaciones: true,
};

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const filterHeaderSx = {
  px: { xs: 2, md: 3 },
  py: 1.6,
  borderBottom: `1px solid ${FP.color.border}`,
  bgcolor: '#FAFBFE',
  display: 'flex',
  alignItems: 'center',
  gap: 1.2,
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.74rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  bgcolor: FP.color.surfaceAlt,
  borderBottom: `1px solid ${FP.color.border}`,
  py: 1.2,
};

const btnOutlinedSx = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 1.4,
  borderColor: FP.color.border,
  color: FP.color.textPrimary,
  bgcolor: '#FFFFFF',
  '&:hover': { borderColor: FP.color.borderStrong, bgcolor: FP.color.surfaceAlt },
};

const RegistroCompra = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const permisoLeer = useMemo(
    () => tienePermisosProgramaCompras(roleDetalles, PROGRAMA_REGISTRO_COMPRAS),
    [roleDetalles]
  );
  const permisoModificar = useMemo(
    () =>
      puedeEditarProgramaFacturacion(roleDetalles, PROGRAMA_REGISTRO_COMPRAS) ||
      tienePermisosProgramaCompras(roleDetalles, PROGRAMA_REGISTRO_COMPRAS),
    [roleDetalles]
  );

  const [productos, setProductos] = useState([]);
  const [reglasDescuentos, setReglasDescuentos] = useState([]);
  const [reglasDescuentosGenerales, setReglasDescuentosGenerales] = useState([]);
  const [suplidores, setSuplidores] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const [tipoCompra, setTipoCompra] = useState('credito');
  const [suplidorSeleccionado, setSuplidorSeleccionado] = useState(null);
  const [configSuplidor, setConfigSuplidor] = useState(null);
  const [busquedaSuplidor, setBusquedaSuplidor] = useState('');
  const [itemsCompra, setItemsCompra] = useState([]);
  const [impuestosCompra, setImpuestosCompra] = useState([]);
  const [observaciones, setObservaciones] = useState('');
  const [busquedaProducto, setBusquedaProducto] = useState('');
  const [dialogoProductoAbierto, setDialogoProductoAbierto] = useState(false);
  const [cantidadAgregar, setCantidadAgregar] = useState(1);
  const [cantidadesPorProducto, setCantidadesPorProducto] = useState({});

  const getCantidadProducto = (codigo) => cantidadesPorProducto[codigo] ?? 1;

  const setCantidadProducto = (codigo, valor) => {
    const num = Math.max(0.01, parseFloat(valor) || 1);
    setCantidadesPorProducto((prev) => ({ ...prev, [codigo]: num }));
  };

  const [fechaEmision, setFechaEmision] = useState(() => new Date().toISOString().split('T')[0]);
  const [fechaVencimiento, setFechaVencimiento] = useState(() => new Date().toISOString().split('T')[0]);

  const [vistaCompraAbierta, setVistaCompraAbierta] = useState(false);
  const [datosVistaCompra, setDatosVistaCompra] = useState({});
  const [tabActual, setTabActual] = useState('registro');

  const totalDescuentosLinea = itemsCompra.reduce((sum, item) => sum + (parseFloat(item.descuento) || 0), 0);
  const subtotalAntesDescuentos = itemsCompra.reduce(
    (sum, item) => sum + (parseFloat(item.cantidad) || 0) * (parseFloat(item.precio) || 0),
    0
  );
  const subtotal = itemsCompra.reduce((sum, item) => sum + (parseFloat(item.subtotal) || 0), 0);

  const reglaDescuentoGeneralAplicada = useMemo(() => {
    const tipoDocCompra = tipoCompra === 'credito' ? TIPO_COMPRA_CREDITO : TIPO_COMPRA_CONTADO;
    const reglasDoc = (reglasDescuentosGenerales || []).filter(
      (r) =>
        r.activa === 'S' &&
        (r.tipo_documento === tipoDocCompra ||
          r.tipo_documento === TIPO_COMPRA_CREDITO ||
          r.tipo_documento === TIPO_COMPRA_CONTADO ||
          r.tipo_documento === TIPO_FACTURA_CREDITO ||
          r.tipo_documento === TIPO_FACTURA_CONTADO)
    );
    const aplicables = reglasDoc.filter(
      (r) =>
        r.aplicar_solo_por_encima_monto !== 'S' ||
        (r.monto_minimo != null && subtotal >= parseFloat(r.monto_minimo))
    );
    if (aplicables.length === 0) return null;
    aplicables.sort((a, b) => (parseFloat(b.monto_minimo) || 0) - (parseFloat(a.monto_minimo) || 0));
    return aplicables[0];
  }, [reglasDescuentosGenerales, subtotal, tipoCompra]);

  const descuentoGeneralTotal = useMemo(() => {
    if (!reglaDescuentoGeneralAplicada || subtotal <= 0) return 0;
    const pct = parseFloat(reglaDescuentoGeneralAplicada.porcentaje) || 0;
    return subtotal * (pct / 100);
  }, [reglaDescuentoGeneralAplicada, subtotal]);

  const totalDescuentos = totalDescuentosLinea + descuentoGeneralTotal;
  const totalImpuestos = impuestosCompra.reduce((sum, imp) => sum + (parseFloat(imp.monto) || 0), 0);
  const total = subtotal - descuentoGeneralTotal + totalImpuestos;

  const getDescuentoGeneralPorItem = (_item) => 0;

  const credenciales = () => ({
    id_empresa: selectedCompany,
    usuario: usuarioLog || '',
    password: claveLog || '',
  });

  useEffect(() => {
    if (permisoLeer) cargarDatos();
  }, [permisoLeer, selectedCompany]);

  useEffect(() => {
    if (subtotal > 0) calcularImpuestos();
  }, [subtotal, descuentoGeneralTotal, itemsCompra]);

  useEffect(() => {
    if (suplidorSeleccionado?.id) cargarConfigSuplidor(suplidorSeleccionado.id);
    else setConfigSuplidor(null);
  }, [suplidorSeleccionado?.id]);

  const cargarDatos = async () => {
    if (!permisoLeer || !selectedCompany?.db_empresa) return;
    setCargando(true);
    try {
      await Promise.all([
        cargarProductos(),
        cargarReglasDescuentos(),
        cargarReglasDescuentosGenerales(),
        cargarSuplidores(),
      ]);
    } catch (e) {
      showError('Error al cargar datos');
    } finally {
      setCargando(false);
    }
  };

  const cargarProductos = async () => {
    try {
      const response = await fetch(servidor + '/api/comprasbackend_obtenerproductos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credenciales(),
          anomesfacturacion: selectedCompany?.anomesfacturacion,
          localidad: selectedCompany?.almacenpordefecto,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setProductos(data.productos || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const cargarConfiguracionPlantilla = async () => {
    try {
      const response = await fetch(servidor + '/api/facturabackend_obtenerconfiguracion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credenciales(),
          tipo_documento: 'COMPRA',
          tipo_factura: 'COMPRA',
        }),
      });
      const data = await response.json();
      if (data.configuracion) {
        return { ...CONFIGURACION_PLANTILLA_DEFAULT, ...data.configuracion };
      }
    } catch (e) {
      console.error(e);
    }
    return CONFIGURACION_PLANTILLA_DEFAULT;
  };

  const cargarReglasDescuentos = async () => {
    try {
      const response = await fetch(servidor + '/api/productosbackend_obtenerreglasdescuentos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credenciales(),
          tabla_inventario: selectedCompany?.anomesfacturacion,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setReglasDescuentos(data.reglas || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const cargarReglasDescuentosGenerales = async () => {
    try {
      const response = await fetch(servidor + '/api/facturabackend_obtenerreglasdescuentosgenerales', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credenciales()),
      });
      if (response.ok) {
        const data = await response.json();
        setReglasDescuentosGenerales(data.reglas || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const cargarSuplidores = async () => {
    try {
      const response = await fetch(servidor + '/api/clientesbackend_buscar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credenciales(),
          tipo: 'Suplidor',
          descripcion: '',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setSuplidores(data.resultados || data.clientes || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const cargarConfigSuplidor = async (suplidorId) => {
    try {
      const response = await fetch(servidor + '/api/clientesbackend_obtenerporid', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credenciales(),
          id: suplidorId,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setConfigSuplidor(data.cliente || null);
      } else {
        setConfigSuplidor(null);
      }
    } catch (e) {
      setConfigSuplidor(null);
    }
  };

  const calcularImpuestos = async () => {
    if (subtotal <= 0) {
      setImpuestosCompra([]);
      return;
    }
    try {
      const response = await fetch(servidor + '/api/facturabackend_calcularimpuestos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credenciales(),
          subtotal: subtotal - descuentoGeneralTotal,
          itemsfactura: itemsCompra,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.impuestos && Array.isArray(data.impuestos)) {
          setImpuestosCompra(
            data.impuestos.map((imp) => ({
              id: imp.id || imp.codigo || Date.now() + Math.random(),
              codigo: imp.codigo || imp.nombre || 'IMP',
              nombre: imp.nombre || imp.descripcion || 'Impuesto',
              porcentaje: parseFloat(imp.porcentaje) || 0,
              monto: parseFloat(imp.monto) || 0,
            }))
          );
        } else {
          setImpuestosCompra([]);
        }
      }
    } catch (e) {
      setImpuestosCompra([]);
    }
  };

  const aplicarReglasDescuento = (producto, cantidad = 1) => {
    let descuentoTotal = 0;
    const precioUnitario = parseFloat(producto.precio) || 0;
    const reglasAplicables = reglasDescuentos.filter((regla) => {
      if (regla.tipoAplicacion === 'producto') return reglaDescuentoAplicaAProducto(regla, producto);
      if (regla.tipoAplicacion === 'departamento' && producto.departamento && regla.departamento === producto.departamento)
        return true;
      return false;
    });
    if (reglasAplicables.length > 0) {
      const regla = reglasAplicables[0];
      if (regla.tipoDescuento === 'porcentaje') {
        const pct = parseFloat(regla.porcentaje) || 0;
        descuentoTotal = precioUnitario * (pct / 100) * cantidad;
      } else if (regla.tipoDescuento === '2x1') {
        const pares = Math.floor(cantidad / 2);
        descuentoTotal = pares * precioUnitario;
      }
    }
    return descuentoTotal;
  };

  const agregarProducto = (producto, cantidad = 1) => {
    if (!permisoModificar) {
      showError('No tiene permiso para modificar compras');
      return;
    }
    const cant = Math.max(0.01, parseFloat(cantidad) || 1);
    const descuento = aplicarReglasDescuento(producto, cant);
    const subtotalItem = Math.max(0, cant * (parseFloat(producto.precio) || 0) - descuento);
    const nuevoItem = {
      id: Date.now() + Math.random(),
      codigo: producto.codigo,
      descripcion: producto.descripcion,
      tipo: 'producto',
      cantidad: cant,
      precio: parseFloat(producto.precio) || 0,
      descuento,
      descuento_general: 0,
      subtotal: subtotalItem,
      existencia: parseFloat(producto.existencia) || 0,
      departamento: producto.departamento || null,
      impuesto: producto.impuesto || null,
      nombreimpuesto: producto.nombreimpuesto || null,
      tipoproducto: 'item',
      medida: producto.medida || 'UND',
      unidad: producto.medida || 'UND',
    };
    const existente = itemsCompra.find((i) => i.codigo === producto.codigo && i.tipo === 'producto');
    if (existente) {
      const nuevaCant = parseFloat(existente.cantidad) + cant;
      const precioLinea = parseFloat(existente.precio) || parseFloat(producto.precio) || 0;
      const productoBase = productos.find((p) => p.codigo === producto.codigo) || producto;
      const productoConPrecio = { ...productoBase, precio: precioLinea };
      const desc = aplicarReglasDescuento(productoConPrecio, nuevaCant);
      setItemsCompra((prev) =>
        prev.map((item) =>
          item.id === existente.id
            ? {
                ...item,
                cantidad: nuevaCant,
                precio: precioLinea,
                descuento: desc,
                subtotal: Math.max(0, nuevaCant * precioLinea - desc),
              }
            : item
        )
      );
    } else {
      setItemsCompra((prev) => [...prev, nuevoItem]);
    }
    setDialogoProductoAbierto(false);
    setBusquedaProducto('');
    setCantidadAgregar(1);
    setCantidadesPorProducto((prev) => {
      const { [producto.codigo]: _omit, ...rest } = prev;
      return rest;
    });
  };

  const eliminarItem = (id) => {
    if (!permisoModificar) return;
    setItemsCompra((prev) => prev.filter((item) => item.id !== id));
  };

  const actualizarCantidad = (id, nuevaCantidad) => {
    if (!permisoModificar) return;
    const num = parseFloat(nuevaCantidad);
    if (isNaN(num) || num <= 0) return;
    setItemsCompra((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const productoBase = productos.find((p) => p.codigo === item.codigo) || item;
        const precio = parseFloat(item.precio) || 0;
        const productoConPrecio = { ...productoBase, precio };
        const desc = aplicarReglasDescuento(productoConPrecio, num);
        const sub = Math.max(0, num * precio - desc);
        return { ...item, cantidad: num, descuento: desc, subtotal: sub };
      })
    );
  };

  const actualizarPrecio = (id, valor) => {
    if (!permisoModificar) return;
    const raw = String(valor ?? '').replace(',', '.');
    const num = parseFloat(raw);
    if (isNaN(num) || num < 0) return;
    setItemsCompra((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const productoBase = productos.find((p) => p.codigo === item.codigo) || item;
        const productoConPrecio = { ...productoBase, precio: num };
        const cant = parseFloat(item.cantidad) || 0;
        const desc = aplicarReglasDescuento(productoConPrecio, cant);
        const sub = Math.max(0, cant * num - desc);
        return { ...item, precio: num, descuento: desc, subtotal: sub };
      })
    );
  };

  const validarSuplidorYLimites = () => {
    if (!suplidorSeleccionado) {
      showError('Seleccione un suplidor');
      return false;
    }
    if (!configSuplidor) return true;
    const limCredito = parseFloat(configSuplidor.limcredito) || 0;
    const limCompra = parseFloat(configSuplidor.limcompra) || 0;
    if (tipoCompra === 'credito' && limCredito > 0 && total > limCredito) {
      showError(`El total (${total.toFixed(2)}) supera el límite de crédito del suplidor (${limCredito.toFixed(2)})`);
      return false;
    }
    if (limCompra > 0 && total > limCompra) {
      showError(`El total (${total.toFixed(2)}) supera el límite de compra del suplidor (${limCompra.toFixed(2)})`);
      return false;
    }
    return true;
  };

  const guardarCompra = async () => {
    if (!permisoModificar) {
      showError('No tiene permiso para registrar compras');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    if (itemsCompra.length === 0) {
      showError('Agregue al menos un ítem');
      return;
    }
    if (!fechaEmision || !fechaVencimiento) {
      showError('Indique fecha de emisión y fecha de vencimiento');
      return;
    }
    if (new Date(fechaVencimiento) < new Date(fechaEmision)) {
      showError('La fecha de vencimiento no puede ser anterior a la fecha de emisión');
      return;
    }
    if (!validarSuplidorYLimites()) return;

    setGuardando(true);
    try {
      const itemsConDescuentoGeneral = itemsCompra.map((item) => {
        const descGen = getDescuentoGeneralPorItem(item);
        return {
          ...item,
          descuento_general: descGen,
          subtotal: Math.max(0, (parseFloat(item.subtotal) || 0) - descGen),
        };
      });
      const subtotalNeto = subtotal - descuentoGeneralTotal;
      const response = await fetch(servidor + '/api/comprasbackend_guardarcompra', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credenciales(),
          anomesfacturacion: selectedCompany?.anomesfacturacion,
          compra: {
            suplidor: String(suplidorSeleccionado.id),
            items: itemsConDescuentoGeneral,
            impuestos: impuestosCompra,
            subtotal: subtotalNeto,
            total_descuentos: totalDescuentos,
            total_impuestos: totalImpuestos,
            total,
            tipo_documento: '',
            tipo_compra: tipoCompra === 'credito' ? TIPO_COMPRA_CREDITO : TIPO_COMPRA_CONTADO,
            fecha_emision: fechaEmision,
            fecha_vencimiento: fechaVencimiento,
            usuario: usuarioLog || '',
            observaciones: observaciones.trim(),
          },
        }),
      });
      if (response.ok) {
        const data = await response.json();
        showSuccess(data.mensaje || `Compra ${data.numero} guardada`);
        const configuracion = await cargarConfiguracionPlantilla();
        const itemsVista = itemsConDescuentoGeneral.map((i) => ({
          ...i,
          descuento_general: getDescuentoGeneralPorItem(i),
        }));
        setDatosVistaCompra({
          items: itemsVista,
          impuestos: impuestosCompra,
          subtotal: subtotalNeto,
          totalDescuentos,
          totalImpuestos,
          total,
          documento: data.documento || 'COMPRA',
          numero: String(data.numero || ''),
          fecha: fechaEmision,
          fechaVencimiento,
          ncf: '',
          tipoDocumento: tipoCompra === 'credito' ? TIPO_COMPRA_CREDITO : TIPO_COMPRA_CONTADO,
          observaciones,
          empresa: selectedCompany?.empresa || selectedCompany?.nombre || selectedCompany,
          id_empresa: selectedCompany,
          tipo_factura: 'COMPRA',
          cliente_nombre: suplidorSeleccionado?.descrip || suplidorSeleccionado?.descripcion || '',
          configuracion,
        });
        setVistaCompraAbierta(true);
        limpiarFormulario();
      } else {
        const err = await response.json();
        showError(err.Error || 'Error al guardar la compra');
      }
    } catch (e) {
      showError('Error de conexión al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const limpiarItems = () => {
    setItemsCompra([]);
    setImpuestosCompra([]);
  };

  const limpiarFormulario = () => {
    limpiarItems();
    setObservaciones('');
    const hoy = new Date().toISOString().split('T')[0];
    setFechaEmision(hoy);
    setFechaVencimiento(hoy);
  };

  const abrirVistaCompra = async () => {
    if (itemsCompra.length === 0) {
      showError('Agregue al menos un ítem para ver la vista previa');
      return;
    }
    if (!suplidorSeleccionado) {
      showError('Seleccione un suplidor');
      return;
    }
    const configuracion = await cargarConfiguracionPlantilla();
    const itemsConDescuentoGeneral = itemsCompra.map((item) => ({
      ...item,
      subtotal: Math.max(0, (parseFloat(item.subtotal) || 0) - getDescuentoGeneralPorItem(item)),
      descuento_general: getDescuentoGeneralPorItem(item),
    }));
    setDatosVistaCompra({
      items: itemsConDescuentoGeneral,
      impuestos: impuestosCompra,
      subtotal: subtotal - descuentoGeneralTotal,
      totalDescuentos,
      totalImpuestos,
      total,
      documento: 'COMPRA',
      numero: '',
      fecha: fechaEmision,
      fechaVencimiento,
      ncf: '',
      tipoDocumento: tipoCompra === 'credito' ? TIPO_COMPRA_CREDITO : TIPO_COMPRA_CONTADO,
      observaciones,
      empresa: selectedCompany?.empresa || selectedCompany?.nombre || selectedCompany,
      id_empresa: selectedCompany,
      tipo_factura: 'COMPRA',
      cliente_nombre: suplidorSeleccionado?.descrip || suplidorSeleccionado?.descripcion || '',
      configuracion,
    });
    setVistaCompraAbierta(true);
  };

  const productosFiltrados = useMemo(() => {
    if (!busquedaProducto.trim()) return productos.slice(0, 50);
    const b = busquedaProducto.toLowerCase();
    return productos
      .filter(
        (p) =>
          String(p.codigo || '').toLowerCase().includes(b) || String(p.descripcion || '').toLowerCase().includes(b)
      )
      .slice(0, 50);
  }, [productos, busquedaProducto]);

  const suplidoresFiltrados = useMemo(() => {
    if (!busquedaSuplidor.trim()) return suplidores.slice(0, 100);
    const b = busquedaSuplidor.toLowerCase();
    return suplidores
      .filter(
        (c) =>
          String(c.id || '').includes(b) ||
          String(c.descrip || c.descripcion || '').toLowerCase().includes(b)
      )
      .slice(0, 100);
  }, [suplidores, busquedaSuplidor]);

  if (!permisoLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Registro de compras"
          subtitle={nombreNegocio ? `${nombreNegocio} · Órdenes de compra` : 'Órdenes de compra'}
        />
        <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{
              ...cardShellSx,
              maxWidth: 520,
              width: '100%',
              textAlign: 'center',
              p: { xs: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: FP.color.textPrimary, mb: 1 }}>
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «registrocompras» (o umbrella «mantenimientocompras») en roles.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title={
          tabActual === 'seguimiento'
            ? 'Seguimiento de compras'
            : tipoCompra === 'credito'
              ? 'Compra a crédito'
              : 'Compra al contado'
        }
        subtitle={
          tabActual === 'seguimiento'
            ? nombreNegocio
              ? `${nombreNegocio} · Órdenes y recepción`
              : 'Órdenes y recepción'
            : nombreNegocio
              ? `${nombreNegocio} · Registro de compras`
              : 'Registro de compras'
        }
        cta={
          tabActual === 'registro' ? (
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={abrirVistaCompra}
              disabled={itemsCompra.length === 0 || !suplidorSeleccionado}
              sx={btnOutlinedSx}
            >
              Vista previa / Imprimir
            </Button>
          ) : null
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Tabs
            value={tabActual}
            onChange={(_, v) => setTabActual(v)}
            sx={{
              minHeight: 48,
              borderBottom: `1px solid ${FP.color.border}`,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.92rem',
                color: FP.color.textSecondary,
                minHeight: 48,
                px: 2.4,
              },
              '& .MuiTab-root.Mui-selected': { color: FP.color.navy },
              '& .MuiTabs-indicator': { backgroundColor: FP.color.navy, height: 3 },
            }}
          >
            <Tab
              value="registro"
              icon={<EditIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Registro"
            />
            <Tab
              value="seguimiento"
              icon={<TimelineIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Seguimiento"
            />
          </Tabs>
        </Card>

        {tabActual === 'seguimiento' && <Detallecompras />}

        {tabActual === 'registro' && (
        <>
        <Card elevation={0} sx={{ ...cardShellSx, mb: 2.5 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LocalMallIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Datos de la orden
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5} alignItems="flex-start">
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  options={suplidoresFiltrados}
                  getOptionLabel={(opt) => `${opt.id || ''} - ${opt.descrip || opt.descripcion || ''}`}
                  value={suplidorSeleccionado}
                  onChange={(_, val) => setSuplidorSeleccionado(val)}
                  inputValue={busquedaSuplidor}
                  onInputChange={(_, val) => setBusquedaSuplidor(val)}
                  disabled={!permisoModificar}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Suplidor"
                      required
                      placeholder="Buscar suplidor…"
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                />
                {configSuplidor && (
                  <Alert
                    severity="info"
                    sx={{
                      mt: 1.5,
                      borderRadius: 1.4,
                      border: `1px solid ${FP.color.border}`,
                      bgcolor: FP.color.surfaceAlt,
                    }}
                  >
                    Límite crédito: {Number(configSuplidor.limcredito || 0).toFixed(2)} · Límite compra:{' '}
                    {Number(configSuplidor.limcompra || 0).toFixed(2)}
                  </Alert>
                )}
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Observaciones"
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  placeholder="Observaciones de la compra…"
                  disabled={!permisoModificar}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mt: 2, ...fpInputSx }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Fecha de emisión"
                  type="date"
                  value={fechaEmision}
                  onChange={(e) => setFechaEmision(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                  disabled={!permisoModificar}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Fecha de vencimiento"
                  type="date"
                  value={fechaVencimiento}
                  onChange={(e) => setFechaVencimiento(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                  disabled={!permisoModificar}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={fpInputSx} disabled={!permisoModificar}>
                  <InputLabel shrink>Tipo de compra</InputLabel>
                  <Select
                    value={tipoCompra}
                    label="Tipo de compra"
                    onChange={(e) => setTipoCompra(e.target.value)}
                  >
                    <MenuItem value="credito">Crédito</MenuItem>
                    <MenuItem value="contado">Contado</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ShoppingCartIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Items de la factura
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogoProductoAbierto(true)}
              disabled={cargando || !permisoModificar}
              size="small"
              sx={{
                bgcolor: FP.color.navy,
                color: '#FFFFFF',
                borderRadius: 1.4,
                textTransform: 'none',
                fontWeight: 700,
                boxShadow: 'none',
                '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
              }}
            >
              Agregar producto
            </Button>
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: `1px solid ${FP.color.border}`,
                borderRadius: 2,
                overflow: 'auto',
                maxHeight: 360,
              }}
            >
              <Table size="small" stickyHeader>
                <TableBody>
                  <TableRow
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      '& td': {
                        border: 'none',
                        borderBottom: `2px solid ${FP.color.border}`,
                        bgcolor: FP.color.surfaceAlt,
                      },
                    }}
                  >
                    <TableCell sx={cellHeadSx}>Código</TableCell>
                    <TableCell sx={cellHeadSx}>Descripción</TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                      Cant.
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
                      Precio
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
                      Descuento
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
                      Subtotal
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                      Acción
                    </TableCell>
                  </TableRow>
                  {itemsCompra.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.92rem' }}>
                          No hay items en la factura. Use «Agregar producto».
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    itemsCompra.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Chip
                            label={row.codigo}
                            size="small"
                            sx={{
                              bgcolor: FP.color.accentBlueSoft,
                              color: FP.color.accentBlue,
                              fontWeight: 700,
                              fontSize: '0.74rem',
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 500, color: FP.color.textPrimary, fontSize: '0.86rem' }}
                          title={row.descripcion}
                        >
                          {row.descripcion}
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            inputProps={{ min: 0.01, step: 0.1, style: { textAlign: 'center' } }}
                            value={row.cantidad}
                            disabled={!permisoModificar}
                            onChange={(e) => actualizarCantidad(row.id, e.target.value)}
                            sx={{ width: 90, ...fpInputSx }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                            value={row.precio}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '' || v === '-') return;
                              actualizarPrecio(row.id, v);
                            }}
                            disabled={!permisoModificar}
                            sx={{ width: 110, ...fpInputSx }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {parseFloat(row.descuento || 0) > 0 ? (
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: FP.color.danger,
                                fontVariantNumeric: 'tabular-nums',
                                fontSize: '0.86rem',
                              }}
                            >
                              -${fmtMoney(row.descuento || 0)}
                            </Typography>
                          ) : (
                            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.86rem' }}>
                              $0.00
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 800,
                            color: FP.color.navy,
                            fontVariantNumeric: 'tabular-nums',
                            fontSize: '0.92rem',
                          }}
                        >
                          ${fmtMoney(row.subtotal)}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => eliminarItem(row.id)}
                            disabled={!permisoModificar}
                            sx={{
                              color: FP.color.danger,
                              '&:hover': { bgcolor: FP.color.dangerSoft },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                mt: 2,
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${FP.color.border}`,
                bgcolor: '#FAFBFE',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ fontWeight: 600, color: FP.color.textSecondary }}>
                  Subtotal:
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: FP.color.textPrimary,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  ${fmtMoney(subtotalAntesDescuentos)}
                </Typography>
              </Box>
              {totalDescuentos > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ fontWeight: 600, color: FP.color.textSecondary, pl: 1 }}>
                    Total descuentos:
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: FP.color.danger,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    -${fmtMoney(totalDescuentos)}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 1.5,
                  pb: 1,
                  borderBottom: `1px dashed ${FP.color.border}`,
                }}
              >
                <Typography sx={{ fontWeight: 600, color: FP.color.textSecondary }}>
                  Subtotal después de descuentos:
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: FP.color.textPrimary,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  ${fmtMoney(subtotal - descuentoGeneralTotal)}
                </Typography>
              </Box>
              {impuestosCompra.length > 0 ? (
                <>
                  {impuestosCompra.map((imp) => (
                    <Box key={imp.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ fontWeight: 500, color: FP.color.textSecondary, pl: 2 }}>
                        {imp.nombre}
                        {imp.porcentaje ? ` (${parseFloat(imp.porcentaje).toFixed(2)}%)` : ''}:
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: FP.color.textPrimary,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        ${fmtMoney(imp.monto || 0)}
                      </Typography>
                    </Box>
                  ))}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1.5,
                      mt: 1,
                      pt: 1,
                      borderTop: `1px solid ${FP.color.border}`,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary }}>
                      Total impuestos:
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: FP.color.textPrimary,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      ${fmtMoney(totalImpuestos)}
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography sx={{ fontWeight: 600, color: FP.color.textSecondary }}>
                    Impuestos:
                  </Typography>
                  <Typography sx={{ fontWeight: 600, color: FP.color.textMuted }}>$0.00</Typography>
                </Box>
              )}
              <Divider sx={{ my: 1.5, borderColor: FP.color.border }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  borderRadius: 1.6,
                  bgcolor: FP.color.navy,
                  color: '#FFFFFF',
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>Total:</Typography>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.5rem',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  ${fmtMoney(total)}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2.5 }}>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={abrirVistaCompra}
                disabled={itemsCompra.length === 0 || !suplidorSeleccionado}
                sx={{
                  height: 50,
                  borderColor: FP.color.border,
                  color: FP.color.textPrimary,
                  bgcolor: '#FFFFFF',
                  borderRadius: 1.4,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                }}
              >
                Vista previa
              </Button>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={limpiarFormulario}
                disabled={!permisoModificar}
                fullWidth
                sx={{
                  height: 50,
                  flex: 1,
                  borderColor: FP.color.border,
                  color: FP.color.textPrimary,
                  bgcolor: '#FFFFFF',
                  borderRadius: 1.4,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                }}
              >
                Limpiar
              </Button>
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={guardarCompra}
                fullWidth
                disabled={
                  guardando ||
                  itemsCompra.length === 0 ||
                  !suplidorSeleccionado ||
                  !permisoModificar ||
                  !fechaEmision ||
                  !fechaVencimiento
                }
                sx={{
                  height: 50,
                  flex: 1,
                  bgcolor: FP.color.navy,
                  color: '#FFFFFF',
                  borderRadius: 1.4,
                  textTransform: 'none',
                  fontWeight: 800,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                  '&.Mui-disabled': { bgcolor: FP.color.surfaceAlt, color: FP.color.textMuted },
                }}
              >
                {guardando ? 'Guardando…' : 'Guardar compra'}
              </Button>
            </Box>
          </CardContent>
        </Card>
        </>
        )}
      </Box>

      <Dialog
        open={dialogoProductoAbierto}
        onClose={() => {
          setDialogoProductoAbierto(false);
          setCantidadesPorProducto({});
          setBusquedaProducto('');
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
          }}
        >
          <Typography
            component="span"
            sx={{ fontWeight: 800, fontSize: '1.05rem', color: FP.color.textPrimary }}
          >
            Agregar producto
          </Typography>
          <IconButton
            onClick={() => {
              setDialogoProductoAbierto(false);
              setCantidadesPorProducto({});
              setBusquedaProducto('');
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: FP.color.surface, p: 3, pt: 3.5 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              mb: 2.5,
              alignItems: 'center',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}
          >
            <Autocomplete
              fullWidth
              freeSolo
              autoHighlight
              openOnFocus
              clearOnBlur={false}
              selectOnFocus
              handleHomeEndKeys
              options={productos}
              filterOptions={(opts, { inputValue }) => {
                const b = String(inputValue || '').trim().toLowerCase();
                if (!b) return opts.slice(0, 50);
                return opts
                  .filter(
                    (p) =>
                      String(p.codigo || '').toLowerCase().includes(b) ||
                      String(p.descripcion || '').toLowerCase().includes(b)
                  )
                  .slice(0, 50);
              }}
              getOptionLabel={(opt) =>
                typeof opt === 'string'
                  ? opt
                  : `${opt.codigo || ''} - ${opt.descripcion || ''}`
              }
              isOptionEqualToValue={(opt, val) => opt?.codigo === val?.codigo}
              inputValue={busquedaProducto}
              onInputChange={(_, val, reason) => {
                if (reason === 'reset') return;
                setBusquedaProducto(val);
              }}
              onChange={(_, val) => {
                if (val && typeof val !== 'string') {
                  const cant = getCantidadProducto(val.codigo) || cantidadAgregar;
                  agregarProducto(val, cant);
                }
              }}
              noOptionsText="No hay productos que coincidan"
              renderOption={(props, opt) => (
                <Box
                  component="li"
                  {...props}
                  key={opt.codigo}
                  sx={{
                    display: 'flex !important',
                    alignItems: 'center !important',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    py: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                    <Chip
                      label={opt.codigo}
                      size="small"
                      sx={{
                        bgcolor: FP.color.accentBlueSoft,
                        color: FP.color.accentBlue,
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '0.86rem',
                        color: FP.color.textPrimary,
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={opt.descripcion}
                    >
                      {opt.descripcion}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.82rem',
                      color: FP.color.textPrimary,
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                      flexShrink: 0,
                    }}
                  >
                    ${fmtMoney(opt.precio || 0)}
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  label="Buscar producto"
                  placeholder="Por código o descripción…"
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: FP.color.textMuted }} fontSize="small" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              type="number"
              label="Cantidad por defecto"
              value={cantidadAgregar}
              onChange={(e) =>
                setCantidadAgregar(Math.max(0.01, parseFloat(e.target.value) || 1))
              }
              inputProps={{
                min: 0.01,
                step: 0.01,
                style: { textAlign: 'center' },
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', sm: 170 }, flexShrink: 0, ...fpInputSx }}
            />
          </Box>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: `1px solid ${FP.color.border}`,
              borderRadius: 2,
              maxHeight: 400,
              overflow: 'auto',
            }}
          >
            <Table size="small" stickyHeader>
              <TableBody>
                <TableRow
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    '& td': {
                      border: 'none',
                      borderBottom: `2px solid ${FP.color.border}`,
                      bgcolor: FP.color.surfaceAlt,
                    },
                  }}
                >
                  <TableCell sx={{ ...cellHeadSx, width: 110 }}>Código</TableCell>
                  <TableCell sx={cellHeadSx}>Descripción</TableCell>
                  <TableCell sx={{ ...cellHeadSx, textAlign: 'right', width: 110 }} align="right">
                    Precio
                  </TableCell>
                  <TableCell sx={{ ...cellHeadSx, textAlign: 'center', width: 100 }} align="center">
                    Cant.
                  </TableCell>
                  <TableCell sx={{ ...cellHeadSx, width: 110 }} />
                </TableRow>
                {productosFiltrados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.92rem' }}>
                        No hay productos o no coincide la búsqueda.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  productosFiltrados.map((p) => (
                    <TableRow key={p.codigo} hover>
                      <TableCell sx={{ fontSize: '0.84rem', color: FP.color.textPrimary }}>
                        {p.codigo}
                      </TableCell>
                      <TableCell
                        sx={{
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          py: 1.2,
                          fontSize: '0.85rem',
                          color: FP.color.textPrimary,
                        }}
                        title={p.descripcion}
                      >
                        {p.descripcion}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 700,
                          fontVariantNumeric: 'tabular-nums',
                          fontSize: '0.86rem',
                          color: FP.color.textPrimary,
                        }}
                      >
                        ${fmtMoney(p.precio || 0)}
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          size="small"
                          value={getCantidadProducto(p.codigo)}
                          onChange={(e) => setCantidadProducto(p.codigo, e.target.value)}
                          inputProps={{
                            min: 0.01,
                            step: 0.01,
                            style: { textAlign: 'center' },
                          }}
                          sx={{ width: 90, ...fpInputSx }}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => agregarProducto(p, getCantidadProducto(p.codigo))}
                          sx={{
                            bgcolor: FP.color.navy,
                            color: '#FFFFFF',
                            borderRadius: 1.2,
                            textTransform: 'none',
                            fontWeight: 700,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                          }}
                        >
                          Agregar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#FAFBFE', borderTop: `1px solid ${FP.color.border}`, px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setDialogoProductoAbierto(false);
              setCantidadesPorProducto({});
              setBusquedaProducto('');
            }}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderRadius: 1.4,
              fontWeight: 700,
              borderColor: FP.color.border,
              color: FP.color.textPrimary,
              '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <VistaCompra
        open={vistaCompraAbierta}
        onClose={() => setVistaCompraAbierta(false)}
        compraData={datosVistaCompra}
      />
    </Box>
  );
};

export default RegistroCompra;
